/**
 * Route protégée par authentification
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Loading } from './Loading';
import type { PortalType, UserRole } from '../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  portal?: PortalType;
  allowedRoles?: UserRole[];
  loginPath?: string;
  changePasswordPath?: string;
}

export function ProtectedRoute({
  children,
  portal,
  allowedRoles,
  loginPath = '/login',
  changePasswordPath = '/change-password',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, currentPortal } = useAuth();
  const location = useLocation();

  // Afficher un loader pendant le chargement
  if (isLoading) {
    return <Loading fullScreen text="Vérification de l'authentification..." />;
  }

  // Rediriger vers login si non authentifié
  if (!isAuthenticated || !user) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Vérifier si l'utilisateur doit changer son mot de passe
  if (user.mustChangePassword && location.pathname !== changePasswordPath) {
    return <Navigate to={changePasswordPath} replace />;
  }

  // Vérifier le portail si spécifié
  if (portal && currentPortal !== portal) {
    return <Navigate to={loginPath} replace />;
  }

  // Vérifier le rôle si spécifié
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
